import React, { useEffect, useState, useCallback } from 'react';
import {
  ContentLayout,
  Header,
  Container,
  SpaceBetween,
  Grid,
  Box,
  Button,
  Cards,
  ColumnLayout,
  StatusIndicator,
  Badge,
  Spinner,
  Alert,
  Icon,
  Link,
} from '@cloudscape-design/components';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import AppLayoutWrapper from './AppLayoutWrapper';
import './Home.css';

// Map pack names to their Adexcloud purchase page URLs
const ADEXCLOUD_PACK_URLS = {
  'startup & tpe': 'https://predash.adexcloud.dz/dashboard/ai-services/Chatbot%20Startup%20&%20TPE/new-instance',
  'pme': 'https://predash.adexcloud.dz/dashboard/ai-services/Chatbot%20PME/new-instance',
  'pme advanced': 'https://predash.adexcloud.dz/dashboard/ai-services/Chatbot%20PME%20Advanced/new-instance',
  'corporate': 'https://predash.adexcloud.dz/dashboard/ai-services/Chatbot%20Corporate/new-instance',
  'corporate plus': 'https://predash.adexcloud.dz/dashboard/ai-services/Chatbot%20Corporate%20Plus/new-instance',
};

const getAdexcloudPurchaseUrl = (pack) => {
  const key = (pack?.name || '').trim().toLowerCase();
  return ADEXCLOUD_PACK_URLS[key] || null;
};

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const accessToken = user?.access_token || user?.token;

  const [packs, setPacks] = useState([]);
  const [stats, setStats] = useState({
    agents: 0,
    knowledgeBases: 0,
    workflows: 0,
    telephony: 0,
    packs: 0,
  });
  const [quotaStatus, setQuotaStatus] = useState({ resources: {} });
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const [packsResp, purchasesResp, agentsResp, kbsResp, wfsResp, sipResp, quotaResp, balanceResp] = await Promise.all([
        axios.get('/api/packs/', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => ({ data: { data: [] } })),
        axios.get('/api/billing/purchases/', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => ({ data: { data: [] } })),
        axios.get('/api/agents/list/', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => ({ data: [] })),
        axios.get('/api/knowledge-bases/', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => ({ data: { kbs: [] } })),
        axios.get('/api/workflows/', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => ({ data: { workflows: [] } })),
        axios.get('/api/sip/', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => ({ data: { data: [] } })),
        axios.get('/api/billing/quota/', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => ({ data: { data: { resources: {} } } })),
        axios.get('/api/billing/balance/', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => ({ data: { data: null } })),
      ]);

      const packData = packsResp.data?.data || [];
      const purchases = purchasesResp.data?.data || [];
      const agents = Array.isArray(agentsResp.data) ? agentsResp.data : (agentsResp.data?.agents || agentsResp.data?.data || []);
      const kbs = kbsResp.data?.kbs || kbsResp.data?.data || [];
      const wfs = wfsResp.data?.workflows || wfsResp.data?.data || [];
      const sip = sipResp.data?.sip || sipResp.data?.data || [];

      setPacks(packData);
      setStats({
        agents: agents.length,
        knowledgeBases: kbs.length,
        workflows: wfs.length,
        telephony: sip.filter((t) => t.agent_type === 'voice' || t.sip?.phone_number).length,
        packs: purchases.length,
      });
      setQuotaStatus(quotaResp.data?.data || { resources: {} });
      setBalance(balanceResp.data?.data || null);
    } catch (err) {
      setError('Failed to load home data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const heroBackground = 'linear-gradient(135deg, #0a1628 0%, #0f2b4d 35%, #1a5fb4 70%, #3b9dff 100%)';

  const formatCurrency = (value, currency) => {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return value;
    return `${num.toLocaleString()} ${currency}`;
  };

  const defaultPack = packs.find((p) => p.is_default) || packs[0];
  const resources = quotaStatus.resources || {};
  const voiceMinutes = resources.voice_minutes || {};
  const textConversations = resources.text_conversations || {};
  const creditBalance = balance?.credit_balance ?? balance?.balance ?? null;
  const creditCurrency = balance?.currency || 'DZD';
  const formatNum = (n) => (n === undefined || n === null ? 0 : n);

  return (
    <AppLayoutWrapper
      navigationHide={false}
      disableContentPaddings
      content={
        <ContentLayout
          defaultPadding
          headerVariant="high-contrast"
          headerBackgroundStyle={heroBackground}
          header={
            <div className="adexgenie-hero-header">
              <img
                src="/adexgeniebot.png"
                alt="Adexgénie bot"
                className="hero-bot-bg"
              />
              <Box className="hero-content">
                <Grid gridDefinition={[{ colspan: { default: 12, l: 8 } }, { colspan: { default: 12, l: 4 } }]}>
                  <SpaceBetween size="l">
                    <SpaceBetween size="xs">
                      <Badge color="green">Adexgénie Platform</Badge>
                      <div>
                        <h1>Welcome back, {user?.first_name || user?.username || 'User'}</h1>
                        <p className="hero-description">
                          Build, deploy and manage AI agents, knowledge bases, workflows and telephony in one place.
                        </p>
                      </div>
                    </SpaceBetween>
                    <p className="hero-description">
                      Your Adexgénie workspace is ready. Monitor your resources, explore commercial packs, or create a new AI agent from scratch.
                    </p>
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button variant="primary" onClick={() => navigate('/Wizard')}>
                        Create an agent
                      </Button>
                      <Button variant="normal" onClick={() => navigate('/dashboard')}>
                        Go to dashboard
                      </Button>
                    </SpaceBetween>
                  </SpaceBetween>
                  <Box textAlign="right" padding={{ top: 's' }}>
                    <ColumnLayout columns={{ default: 1, s: 2, m: 2, l: 2, xl: 2 }} variant="text-grid">
                      <div>
                        <span className="hero-stat-number">{stats.agents}</span>
                        <span className="hero-stat-label">Agents</span>
                      </div>
                      <div>
                        <span className="hero-stat-number">{stats.knowledgeBases}</span>
                        <span className="hero-stat-label">Knowledge bases</span>
                      </div>
                      <div>
                        <span className="hero-stat-number">{stats.workflows}</span>
                        <span className="hero-stat-label">Workflows</span>
                      </div>
                      <div>
                        <span className="hero-stat-number">{stats.telephony}</span>
                        <span className="hero-stat-label">Telephony</span>
                      </div>
                      <div>
                        <span className="hero-stat-number">{formatNum(voiceMinutes.used)} / {formatNum(voiceMinutes.total)}</span>
                        <span className="hero-stat-label">Voice min</span>
                      </div>
                      <div>
                        <span className="hero-stat-number">{formatNum(textConversations.used)} / {formatNum(textConversations.total)}</span>
                        <span className="hero-stat-label">Text conv.</span>
                      </div>
                    </ColumnLayout>
                  </Box>
                </Grid>
              </Box>
            </div>
          }
        >
          <SpaceBetween size="xl">
            {error && (
              <Alert type="error" header="Error loading data">
                {error}
              </Alert>
            )}

            {/* Quick actions */}
            <Container header={<Header variant="h2">Quick actions</Header>}>
              <Grid gridDefinition={[{ colspan: { default: 12, xs: 6, m: 3 } }, { colspan: { default: 12, xs: 6, m: 3 } }, { colspan: { default: 12, xs: 6, m: 3 } }, { colspan: { default: 12, xs: 6, m: 3 } }]}>
                <Box padding="s">
                  <Link onFollow={() => navigate('/Wizard')}>
                    <SpaceBetween direction="horizontal" size="xs">
                      <Icon name="add-plus" variant="normal" />
                      <span>New agent</span>
                    </SpaceBetween>
                  </Link>
                </Box>
                <Box padding="s">
                  <Link onFollow={() => navigate('/knowledge-base/new')}>
                    <SpaceBetween direction="horizontal" size="xs">
                      <Icon name="file" variant="normal" />
                      <span>New knowledge base</span>
                    </SpaceBetween>
                  </Link>
                </Box>
                <Box padding="s">
                  <Link onFollow={() => navigate('/workflow/new')}>
                    <SpaceBetween direction="horizontal" size="xs">
                      <Icon name="script" variant="normal" />
                      <span>New workflow</span>
                    </SpaceBetween>
                  </Link>
                </Box>
                <Box padding="s">
                  <Link onFollow={() => navigate('/telephony/new')}>
                    <SpaceBetween direction="horizontal" size="xs">
                      <Icon name="phone" variant="normal" />
                      <span>New telephony</span>
                    </SpaceBetween>
                  </Link>
                </Box>
              </Grid>
            </Container>

            {/* Commercial packs */}
            <Container
              header={
                <Header
                  variant="h2"
                  actions={
                    <Button variant="primary" onClick={() => navigate('/Wizard')}>
                      Get started
                    </Button>
                  }
                  description="Choose a plan that fits your needs. Each pack defines quotas for agents, knowledge bases, workflows and phone numbers."
                >
                  Commercial packs
                </Header>
              }
            >
              {loading ? (
                <Box textAlign="center" padding="l">
                  <Spinner size="large" />
                </Box>
              ) : packs.length === 0 ? (
                <Alert type="info" header="No packs available">
                  Contact your administrator to configure commercial packs.
                </Alert>
              ) : (
                <Grid
                  className="commercial-packs-grid"
                  gridDefinition={packs.map(() => ({ colspan: { default: 12, s: 6, m: 6, l: 3 } }))}
                >
                  {packs.map((item) => (
                    <Container
                      key={item.id}
                      fitHeight
                      header={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <SpaceBetween size="xs">
                            <Box variant="h3">{item.name}</Box>
                            <Badge color={item.billing_cycle === 'yearly' ? 'green' : 'grey'}>
                              {item.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'}
                            </Badge>
                          </SpaceBetween>
                          <SpaceBetween size="xs" direction="vertical" alignItems="end">
                            <Box variant="small" color="text-status-inactive">/{item.cycle_label || 'month'}</Box>
                            <Box variant="h2" color="text-status-info">{formatCurrency(item.cycle_price, item.currency)}</Box>
                          </SpaceBetween>
                        </div>
                      }
                      footer={
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            variant="primary"
                            iconName="external"
                            href={getAdexcloudPurchaseUrl(item)}
                            target="_blank"
                            disabled={!getAdexcloudPurchaseUrl(item)}
                          >
                            Buy on Adexcloud
                          </Button>
                        </div>
                      }
                    >
                      <SpaceBetween size="m">
                        <Box color="text-body-secondary">{item.description || 'No description'}</Box>
                        <ColumnLayout columns={{ default: 1, s: 2, m: 2, l: 2, xl: 2 }} variant="text-grid">
                          <div>
                            <Box variant="small">Agents</Box>
                            <Box variant="awsui-key-label">{item.quotas?.max_agents ?? '-'}</Box>
                          </div>
                          <div>
                            <Box variant="small">Knowledge bases</Box>
                            <Box variant="awsui-key-label">{item.quotas?.max_knowledge_bases ?? '-'}</Box>
                          </div>
                          <div>
                            <Box variant="small">Workflows</Box>
                            <Box variant="awsui-key-label">{item.quotas?.max_workflows ?? '-'}</Box>
                          </div>
                          <div>
                            <Box variant="small">Phone numbers</Box>
                            <Box variant="awsui-key-label">{item.quotas?.max_telephony_numbers ?? '-'}</Box>
                          </div>
                          <div>
                            <Box variant="small">Conversations</Box>
                            <Box variant="awsui-key-label">{item.quotas?.cycle_conversations ?? '-'}</Box>
                            <Box variant="small" color="text-body-secondary">per {item.cycle_label || 'month'}</Box>
                          </div>
                          <div>
                            <Box variant="small">Storage</Box>
                            <Box variant="awsui-key-label">{item.quotas?.document_storage_mb ?? '-'} MB</Box>
                          </div>
                        </ColumnLayout>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {item.features?.has_chatbot_widget && <Badge color="green">Widget</Badge>}
                          {item.features?.has_lead_capture && <Badge color="green">Leads</Badge>}
                          {item.features?.has_human_escalation && <Badge color="green">Human handoff</Badge>}
                          {item.features?.has_api_integration && <Badge color="blue">API</Badge>}
                          {item.features?.has_crm_integration && <Badge color="blue">CRM</Badge>}
                          {item.features?.has_erp_integration && <Badge color="blue">ERP</Badge>}
                        </div>
                      </SpaceBetween>
                    </Container>
                  ))}
                </Grid>
              )}
            </Container>

            {/* Current plan / resources summary */}
            <Container
              header={<Header variant="h2" description="Current resource consumption">Resource summary</Header>}
            >
              <Grid gridDefinition={[
                { colspan: { default: 12, s: 6, m: 4 } },
                { colspan: { default: 12, s: 6, m: 4 } },
                { colspan: { default: 12, s: 6, m: 4 } },
              ]}>
                <Box padding="s">
                  <SpaceBetween size="xs">
                    <Box variant="awsui-key-label">Current plan</Box>
                    <Box variant="h2" color="text-status-info">{defaultPack ? defaultPack.name : 'No pack'}</Box>
                    <StatusIndicator type={defaultPack ? 'success' : 'warning'}>
                      {defaultPack ? `${formatCurrency(defaultPack.cycle_price, defaultPack.currency)}/${defaultPack.cycle_label || 'month'}` : 'Not configured'}
                    </StatusIndicator>
                  </SpaceBetween>
                </Box>
                <Box padding="s">
                  <SpaceBetween size="xs">
                    <Box variant="awsui-key-label">Voice minutes</Box>
                    <Box variant="h2" color="text-status-info">
                      {formatNum(voiceMinutes.used)} / {formatNum(voiceMinutes.total)}
                    </Box>
                    <Box variant="small" color={voiceMinutes.total > 0 && voiceMinutes.used >= voiceMinutes.total ? 'text-status-error' : 'text-status-success'}>
                      {formatNum(voiceMinutes.remaining)} remaining
                    </Box>
                  </SpaceBetween>
                </Box>
                <Box padding="s">
                  <SpaceBetween size="xs">
                    <Box variant="awsui-key-label">Text conversations</Box>
                    <Box variant="h2" color="text-status-info">
                      {formatNum(textConversations.used)} / {formatNum(textConversations.total)}
                    </Box>
                    <Box variant="small" color={textConversations.total > 0 && textConversations.used >= textConversations.total ? 'text-status-error' : 'text-status-success'}>
                      {formatNum(textConversations.remaining)} remaining
                    </Box>
                  </SpaceBetween>
                </Box>
              </Grid>
            </Container>
          </SpaceBetween>
        </ContentLayout>
      }
    />
  );
}
